/// <amd-module name="Build/Admin/PoolsList/PoolsListSignalRHub" />
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";

import { HubNames, IPoolsHubServer, IPoolsHubClient, getDTProxyToRegister } from "DistributedTask/Common/SignalR/SignalRHubProxies";

import { SignalRHubBase, ISignalRHubOptionsBase } from "Build/Admin/Common/SignalRHubBase";
import { PoolsListActionCreator } from "Build/Admin/PoolsList/Actions/PoolsListActionCreator";

export interface IPoolsListSignalRHubOptions extends ISignalRHubOptionsBase {
    actionCreator: PoolsListActionCreator;
}

// We create a wrapper class around the hub implementation and make the actual hub a static member of the wrapper class
// to enforce the fact that there should only be one active hub on a particular view at any given time.
export class PoolsListSignalRHub {
    private static hub: PoolsListSignalRHubImpl | undefined;

    constructor(options: IPoolsListSignalRHubOptions) {
        if (!PoolsListSignalRHub.hub) {
            PoolsListSignalRHub.hub = new PoolsListSignalRHubImpl(options);
        }
    }

    public listen(): void {
        if (PoolsListSignalRHub.hub) {
            PoolsListSignalRHub.hub.listen();
        }
    }

    public dispose(): void {
        if (PoolsListSignalRHub.hub) {
            PoolsListSignalRHub.hub.dispose();
            PoolsListSignalRHub.hub = undefined;
        }
    }
}

class PoolsListSignalRHubImpl extends SignalRHubBase<IPoolsHubClient, IPoolsHubServer, IPoolsListSignalRHubOptions> {
    constructor(options: IPoolsListSignalRHubOptions) {
        super(HubNames.poolsHub, options);

        this.initializeProxy(getDTProxyToRegister, this._initializeClient);
        this.addListener(this);
    }

    public listen(): void {
        this.listenTimer = window.setTimeout(
            () => {
                this.subscribePromise = this.start().then(() => {
                    const hub = this.hub;
                    if (hub) {
                        hub.server.subscribe();
                    }
                });
            },
            1000 // run on a brief timeout, to avoid quick switching
        );
    }

    public dispose(): void {
        this.clearTimer();

        const hub = this.hub;
        // If we never called .start() then we don't need to call super.dispose()
        if (hub && this.subscribePromise) {
            this.subscribePromise.then(() => {
                hub.server.unsubscribe()
                    .then(() => super.dispose(), () => super.dispose());
            });
        }
    }

    private _initializeClient = (proxy: ISignalRHubProxy<IPoolsHubClient, IPoolsHubServer>) => {
        proxy.client.notifyAgentRequestUpdated = (requestId: number, poolId: number) => {
            this.options.actionCreator.updatePoolStatus(poolId);
        };
    }
}
